import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { X, MapPin } from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const EditBuildingDialog = ({ open, onClose, building, onSuccess }) => {
  const { getAccessToken } = useAuth();
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [initialBuildingAddress, setInitialBuildingAddress] = useState('');
  const [totalUnits, setTotalUnits] = useState('');
  const [yearBuilt, setYearBuilt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [autocompleteInitialized, setAutocompleteInitialized] = useState(false);

  useEffect(() => {
    const fetchBuildingData = async () => {
      if (building && open && building.id) {
        try {
          const accessToken = getAccessToken();
          const response = await fetch(
            `${process.env.REACT_APP_NODE_URL}/buildings/buildings/${building.id}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.status && data.data) {
              const buildingData = data.data;
              const addressParts = [
                buildingData.address?.street,
                buildingData.address?.city,
                buildingData.address?.state,
                buildingData.address?.zipCode,
              ].filter(Boolean);
              const fullAddress = addressParts.join(', ');

              const resolvedAddress = fullAddress || '';
              setBuildingName(buildingData.buildingName || '');
              setBuildingAddress(resolvedAddress);
              setInitialBuildingAddress(resolvedAddress);
              setTotalUnits(
                buildingData.buildingDetails?.totalUnits?.toString() || ''
              );
              setYearBuilt(
                buildingData.yearBuilt
                  ? dayjs().year(buildingData.yearBuilt)
                  : null
              );
            } else {
              const resolvedAddress = building.address || '';
              setBuildingName(building.name || '');
              setBuildingAddress(resolvedAddress);
              setInitialBuildingAddress(resolvedAddress);
              setTotalUnits(building.totalUnits?.toString() || '');
              setYearBuilt(null);
            }
          } else {
            const resolvedAddress = building.address || '';
            setBuildingName(building.name || '');
            setBuildingAddress(resolvedAddress);
            setInitialBuildingAddress(resolvedAddress);
            setTotalUnits(building.totalUnits?.toString() || '');
            setYearBuilt(null);
          }
        } catch (error) {
          console.error('Error fetching building data:', error);
          const resolvedAddress = building.address || '';
          setBuildingName(building.name || '');
          setBuildingAddress(resolvedAddress);
          setInitialBuildingAddress(resolvedAddress);
          setTotalUnits(building.totalUnits?.toString() || '');
          setYearBuilt(null);
        }
        setError(null);
      }
    };

    fetchBuildingData();
  }, [building, open, getAccessToken]);

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setTimeout(initializeAutocomplete, 500);
        return;
      }

      if (!addressInputRef.current) {
        setTimeout(initializeAutocomplete, 100);
        return;
      }

      if (autocompleteInitialized && autocompleteRef.current) {
        return;
      }

      if (autocompleteRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
          autocompleteRef.current = null;
        } catch (error) {
          console.error('Error cleaning up existing autocomplete:', error);
        }
      }

      try {
        const options = {
          componentRestrictions: { country: 'us' },
          fields: [
            'address_components',
            'geometry',
            'icon',
            'name',
            'formatted_address',
            'place_id',
          ],
        };

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          options,
        );

        const placeChangedHandler = () => {
          setTimeout(() => {
            try {
              const place = autocompleteRef.current?.getPlace();
              if (
                place &&
                place.formatted_address &&
                place.place_id &&
                place.formatted_address.trim() !== ''
              ) {
                setBuildingAddress(place.formatted_address);
              }
            } catch (error) {
              console.error('Error in place_changed handler:', error);
            }
          }, 10);
        };

        autocompleteRef.current.addListener(
          'place_changed',
          placeChangedHandler,
        );

        setAutocompleteInitialized(true);
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        setAutocompleteInitialized(false);
        setTimeout(initializeAutocomplete, 1000);
      }
    };

    if (open && addressInputRef.current) {
      initializeAutocomplete();
    }

    return () => {
      if (autocompleteRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
        } catch (error) {
          console.error('Error cleaning up autocomplete:', error);
        }
        autocompleteRef.current = null;
      }
      setAutocompleteInitialized(false);
    };
  }, [open, autocompleteInitialized]);

  const handleSave = async () => {
    if (!buildingAddress || !totalUnits) {
      setError('Building address and total units are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const accessToken = getAccessToken();
      const updateData = {
        buildingName: buildingName || null,
        totalUnits: parseInt(totalUnits),
        yearBuilt: yearBuilt ? yearBuilt.year() : null,
      };

      const trimmedAddress = (buildingAddress || '').trim();
      const trimmedInitialAddress = (initialBuildingAddress || '').trim();
      if (trimmedAddress && trimmedAddress !== trimmedInitialAddress) {
        updateData.buildingAddress = trimmedAddress;
      }

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/${building.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to update building');
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error updating building:', error);
      setError(error.message || 'Failed to update building. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#111827',
          }}
        >
          Edit Building
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 'auto',
            padding: '0.25rem',
            color: '#6B7280',
          }}
        >
          <X size={20} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                marginBottom: '0.5rem',
                color: '#374151',
              }}
            >
              Building Address *
            </Typography>
            <TextField
              inputRef={addressInputRef}
              fullWidth
              placeholder='123 Main St, Dallas, TX 75201'
              value={buildingAddress}
              onChange={e => setBuildingAddress(e.target.value)}
              InputProps={{
                startAdornment: (
                  <MapPin
                    size={16}
                    style={{ marginRight: '0.5rem', color: '#6B7280' }}
                  />
                ),
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                marginBottom: '0.5rem',
                color: '#374151',
              }}
            >
              Building Name (Optional)
            </Typography>
            <TextField
              fullWidth
              placeholder='e.g., Sunset Apartments'
              value={buildingName}
              onChange={e => setBuildingName(e.target.value)}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                marginBottom: '0.5rem',
                color: '#374151',
              }}
            >
              Total Units *
            </Typography>
            <TextField
              fullWidth
              type='number'
              placeholder='e.g., 24'
              value={totalUnits}
              onChange={e => setTotalUnits(e.target.value)}
              InputProps={{
                inputProps: { min: 1 },
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                marginBottom: '0.5rem',
                color: '#374151',
              }}
            >
              Year Built (Optional)
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={['year']}
                value={yearBuilt}
                onChange={newValue => setYearBuilt(newValue)}
                minDate={dayjs('1800')}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'e.g., 2015',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '1.5rem',
          borderTop: '1px solid #E5E7EB',
          gap: '0.75rem',
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            color: '#374151',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={saving || !buildingAddress || !totalUnits}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#39585C',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#2A3F42' },
            '&:disabled': { backgroundColor: '#9CA3AF' },
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBuildingDialog;

